import React, { ChangeEvent, FC, ReactNode } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Theme, Typography } from '@material-ui/core';
import { MinusIcon, PlusIcon } from 'svgs';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';

export interface AccordionTextBlockProps {
  description?: string;
  title: string;
  text?: string;
  listItems?: Array<{ textItem: ReactNode }>;
  manyBlocks?: boolean;
  listBlocks?: Array<{ block: string; listBlockItems: Array<{ textBlockItem?: string }> }>;
}

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    accordion: {
      boxShadow: 'none',
      border: `1px solid ${theme.palette.secondary.light}`,
      borderRadius: '0 !important',
      padding: theme.spacing(1, 2),
      backgroundColor: theme.palette.common.white,
      marginTop: theme.spacing(2),
      '&:before': {
        display: 'none',
      },
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(1.5, 2),
      },
      '& .MuiAccordionSummary-expandIcon': {
        color: 'transparent',
      },
    },
    accordionDetails: {
      marginTop: theme.spacing(-4),
    },
    accordionDetails1: {
      marginTop: 0,
    },
    description: {
      color: theme.palette.common.black,
      paddingBottom: theme.spacing(3),
    },
    title: {
      color: theme.palette.primary.dark,
      fontSize: 17,
    },
    textBlock: {
      color: theme.palette.primary.dark,
      lineHeight: 1.8,
      '& ul': {
        paddingLeft: theme.spacing(3),
        [theme.breakpoints.up('sm')]: {
          paddingLeft: theme.spacing(4),
        },
      },
    },
    text: {
      color: theme.palette.primary.dark,
      fontSize: 17,
      paddingTop: theme.spacing(3),
    },
    liItem: {
      color: theme.palette.primary.main,
      fontSize: 15,
      lineHeight: 1.8,
      '& span': {
        color: theme.palette.text.secondary,
      },
    },
  });
});

const AccordionTextBlock: FC<AccordionTextBlockProps> = (props) => {
  const { description, title, text, listItems, manyBlocks = false, listBlocks } = props;
  const classes = useStyles();
  const [expanded, setExpanded] = React.useState(false);

  const handleChange = (event: ChangeEvent, isExpanded: boolean) => {
    setExpanded(isExpanded);
  };

  return (
    <Box>
      <Typography className={clsx(description && classes.description)} variant="body1">
        {description}
      </Typography>
      <Accordion expanded={expanded} onChange={handleChange} className={classes.accordion}>
        <AccordionSummary expandIcon={expanded ? <MinusIcon /> : <PlusIcon />}>
          <Typography className={classes.title}>{title}</Typography>
        </AccordionSummary>

        {manyBlocks ? (
          listBlocks.map(({ block, listBlockItems }, index) => (
            <AccordionDetails className={classes.accordionDetails1} key={index}>
              <Box className={classes.textBlock}>
                <Typography className={clsx(text && classes.text)}>{block}</Typography>
                <ul>
                  {listBlockItems.map(({ textBlockItem }, index) => (
                    <li className={classes.liItem} key={index}>
                      <span>{textBlockItem}</span>
                    </li>
                  ))}
                </ul>
              </Box>
            </AccordionDetails>
          ))
        ) : (
          <AccordionDetails className={classes.accordionDetails}>
            <Box className={classes.textBlock}>
              <Typography className={clsx(text && classes.text)}>{text}</Typography>
              <ul>
                {listItems.map(({ textItem }, index) => (
                  <li className={classes.liItem} key={index}>
                    <span>{textItem}</span>
                  </li>
                ))}
              </ul>
            </Box>
          </AccordionDetails>
        )}
      </Accordion>
    </Box>
  );
};

export default AccordionTextBlock;
